import Modal from 'bootstrap/js/dist/modal';
import { initIntlPhone } from './dynamicContentLoader';
import { validateEmail } from './applicationForm';
import { getMarketingAttribution } from './marketingAttribution';
import { trackLeadCreated } from './openaiAdsTracking';
import { setConsentDisclosure } from './progressiveDisclosure';

const API = '/api/method/zoni_edu.zoni_edu.doctype.career_fair_event.career_fair_event.';
const SUBMIT = '/api/method/zoni_edu.zoni_edu.doctype.contacts.contact_submission.submit_contact_form';

export function isCareerFairLink(search = window.location.search) {
    return new URLSearchParams(search).get('utm_source') === 'career_fair';
}

export function safeWhatsAppUrl(value) {
    try {
        const url = new URL(value);
        return url.protocol === 'https:' && url.hostname === 'wa.me'
            && !url.username && !url.password && !url.port && /^\/\d{7,15}$/.test(url.pathname)
            ? url.href : '';
    } catch (_) { return ''; }
}

function genericConfig() {
    return { event: '', event_name: '', booth_label: '', whatsapp_redirect_enabled: false };
}

async function fetchConfig(campaign) {
    try {
        const response = await fetch(`${API}get_career_fair_config?campaign=${encodeURIComponent(campaign)}`, {
            signal: AbortSignal.timeout(5000), headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('config_unavailable');
        const data = await response.json();
        return data.message || genericConfig();
    } catch (_) { return genericConfig(); }
}

export function initCareerFair({ skipAutoModal = false } = {}) {
    const modal = document.getElementById('careerFairModal');
    const thanks = document.getElementById('careerFairThanksModal');
    const form = document.getElementById('career-fair-form');
    if (!modal || !thanks || !form || !isCareerFairLink() || skipAutoModal) return;
    let config = genericConfig();
    let phone = null;
    let capClient = null;
    let countdown = null;
    let busy = false;
    let submitted = false;
    let phoneReady = null;
    const field = name => form.elements.namedItem(name);
    const error = form.querySelector('[data-fair-error]');
    const submit = form.querySelector('[type="submit"]');
    const label = form.querySelector('[data-fair-submit-label]');
    const loading = form.querySelector('[data-fair-loading]');
    const emailPreview = form.querySelector('[data-fair-email-preview]');
    const intro = modal.querySelector('[data-fair-introduction]');
    const genericIntro = intro.textContent;
    const emailSection = document.getElementById('career-fair-email-disclosure');
    const phoneSection = document.getElementById('career-fair-phone-disclosure');
    const messageSection = document.getElementById('career-fair-message-disclosure');
    const messageToggle = form.querySelector('[data-fair-message-toggle]');

    function syncEmail() {
        const valid = Boolean(field('email').value.trim() && field('email').validity.valid);
        setConsentDisclosure(field('email'), emailSection, valid);
        return valid;
    }
    function syncPhone(knownValidity = null) {
        const valid = typeof knownValidity === 'boolean' ? knownValidity : Boolean(phone?.isValidNumber?.());
        setConsentDisclosure(field('phone'), phoneSection, valid);
        return valid;
    }
    function setMessageExpanded(expanded) {
        setConsentDisclosure(messageToggle, messageSection, expanded);
        messageToggle.textContent = expanded ? messageToggle.dataset.hideLabel : messageToggle.dataset.addLabel;
    }
    messageToggle.addEventListener('click', () => {
        const expanded = messageToggle.getAttribute('aria-expanded') !== 'true';
        setMessageExpanded(expanded);
        if (expanded) field('message').focus();
    });
    for (const eventName of ['input', 'change', 'blur']) {
        field('email').addEventListener(eventName, syncEmail);
        field('phone').addEventListener(eventName, () => syncPhone());
    }
    modal.addEventListener('shown.bs.modal', () => { syncEmail(); syncPhone(); });
    form.addEventListener('reset', () => {
        window.setTimeout(() => {
            field('email').setCustomValidity('');
            previousEmail = field('email').value;
            syncEmail();
            syncPhone(false);
            setMessageExpanded(false);
            syncEmailPreview();
        }, 0);
    });
    setConsentDisclosure(field('email'), emailSection, false);
    setConsentDisclosure(field('phone'), phoneSection, false);
    setMessageExpanded(false);

    function showError(message) {
        error.textContent = message || error.dataset.error;
        error.hidden = false;
    }

    function applyConfig(next) {
        config = next;
        intro.textContent = config.event_name
            ? intro.dataset.eventCopy.replace('{event}', config.event_name) : genericIntro;
        for (const badge of document.querySelectorAll('.career-fair-modal [data-fair-booth]')) {
            badge.textContent = config.booth_label || '';
            badge.hidden = !config.booth_label;
        }
        thanks.querySelector('[data-fair-souvenir]').hidden = !config.booth_label;
        field('whatsapp_consent').required = Boolean(config.whatsapp_redirect_enabled);
        const note = form.querySelector('[data-fair-whatsapp-note]');
        note.textContent = config.whatsapp_redirect_enabled ? note.dataset.required : note.dataset.optional;
        modal.querySelector('[data-fair-redirect-disclosure]').hidden = !config.whatsapp_redirect_enabled;
    }

    function syncEmailPreview() {
        emailPreview.hidden = !field('email_marketing_consent').checked;
        emailPreview.textContent = emailPreview.dataset.message.replace('{email}', field('email').value.trim());
    }
    let previousEmail = field('email').value;
    const handleEmailEdit = () => {
        if (field('email').value === previousEmail) return;
        previousEmail = field('email').value;
        field('email').setCustomValidity('');
        field('email_marketing_consent').checked = false;
        syncEmailPreview();
        syncEmail();
    };
    field('email').addEventListener('input', handleEmailEdit);
    field('email').addEventListener('change', handleEmailEdit);
    field('email_marketing_consent').addEventListener('change', syncEmailPreview);

    function cancelRedirect() {
        if (countdown !== null) window.clearInterval(countdown);
        countdown = null;
        thanks.querySelector('[data-fair-chat]').hidden = true;
    }
    thanks.addEventListener('hide.bs.modal', cancelRedirect);
    thanks.querySelector('[data-fair-stay]').addEventListener('click', cancelRedirect);
    thanks.querySelector('[data-fair-go]').addEventListener('click', () => {
        if (countdown !== null) window.clearInterval(countdown);
        countdown = null;
    });

    function showThanks(result) {
        const notice = thanks.querySelector('[data-fair-opt-in]');
        const key = String(result.email_marketing_opt_in_status || '').replace(/_([a-z])/g, (_, c) => c.toUpperCase());
        notice.textContent = notice.dataset[key] || '';
        notice.hidden = !notice.textContent;
        const destination = safeWhatsAppUrl(result.career_fair_whatsapp_url);
        cancelRedirect();
        thanks.addEventListener('shown.bs.modal', () => {
            if (!destination) return;
            let seconds = 5;
            const text = thanks.querySelector('[data-fair-countdown]');
            const update = () => { text.textContent = text.dataset.message.replace('{seconds}', String(seconds)); };
            thanks.querySelector('[data-fair-go]').href = destination;
            thanks.querySelector('[data-fair-chat]').hidden = false;
            update();
            countdown = window.setInterval(() => {
                seconds -= 1;
                if (seconds <= 0) {
                    window.clearInterval(countdown);
                    countdown = null;
                    window.location.assign(destination);
                } else update();
            }, 1000);
        }, { once: true });
        modal.addEventListener('hidden.bs.modal', () => {
            Modal.getOrCreateInstance(thanks).show();
        }, { once: true });
        Modal.getOrCreateInstance(modal).hide();
    }

    async function solveCap() {
        const endpoint = form.querySelector('[data-fair-cap-endpoint]').value.trim();
        if (!endpoint) return '';
        if (!window.Cap) throw new Error('captcha_unavailable');
        capClient ||= new window.Cap({ apiEndpoint: endpoint });
        const result = await capClient.solve();
        const token = result?.token || capClient.token;
        if (typeof token !== 'string' || !token.trim()) throw new Error('captcha_unavailable');
        return token.trim();
    }

    modal.addEventListener('hide.bs.modal', event => {
        // Keep the result transition attached to the form while a submission is in flight.
        if (busy && !submitted) event.preventDefault();
    });
    modal.addEventListener('hidden.bs.modal', () => {
        try { capClient?.reset?.(); } catch (_) { /* No token is reused. */ }
    });
    form.addEventListener('submit', async event => {
        event.preventDefault();
        if (busy || submitted) return;
        error.hidden = true;
        form.classList.add('was-validated');
        busy = true;
        submit.disabled = true;
        label.hidden = true;
        loading.hidden = false;
        let accepted = false;
        try {
            await phoneReady;
            syncEmail();
            const phoneValid = syncPhone();
            // Validate visible contact fields before any required consent checkbox.
            for (const name of ['lead_interest', 'full_name', 'email']) {
                if (!field(name).reportValidity()) return;
            }
            if (!phoneValid) {
                showError(error.dataset.phoneError);
                field('phone').focus();
                return;
            }
            // Disclosures must be visible before the browser focuses missing consent.
            await new Promise(resolve => window.requestAnimationFrame(resolve));
            if (!form.reportValidity()) return;
            if (!await validateEmail('career-fair-email')) return;
            if (field('website').value) return;
            let token;
            try { token = await solveCap(); }
            catch (_) { showError(error.dataset.capError); return; }
            const attribution = getMarketingAttribution();
            const payload = {
                full_name: field('full_name').value.trim(),
                email: field('email').value.trim().toLowerCase(),
                phone: phone.getNumber(), message: field('message').value.trim(),
                lead_interest: field('lead_interest').value,
                is_career_fair: 1, career_fair_event: config.event || '',
                sms_consent: field('sms_consent').checked ? 1 : 0,
                sms_consent_marketing: field('sms_consent_marketing').checked ? 1 : 0,
                whatsapp_consent: field('whatsapp_consent').checked ? 1 : 0,
                email_marketing_consent: field('email_marketing_consent').checked ? 1 : 0,
                email_marketing_consent_version: field('email_marketing_consent').checked
                    ? field('email_marketing_consent').dataset.consentVersion : '',
                language: navigator.language || 'en',
                url_language_code: field('url_language_code').value,
                referrer_url: document.referrer || 'Direct',
                ...(attribution ? { marketing_attribution: attribution } : {}),
            };
            const response = await fetch(SUBMIT, {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json',
                    'X-Frappe-CSRF-Token': window.frappe?.csrf_token || '', 'X-Cap-Token': token },
                body: JSON.stringify({ data: payload, honeypot: field('website').value || '' }),
            });
            const data = await response.json();
            if (!response.ok || !data.message?.ok) {
                // Explain event changes without discarding the visitor's input.
                const current = await fetchConfig(new URLSearchParams(window.location.search).get('utm_campaign') || '');
                if (current.event !== config.event || current.whatsapp_redirect_enabled !== config.whatsapp_redirect_enabled) {
                    applyConfig(current);
                    showError(error.dataset.eventError);
                } else showError(error.dataset.error);
                return;
            }
            accepted = true;
            submitted = true;
            window.dataLayer ||= [];
            window.dataLayer.push({ event: 'form_success_submission', form_name: 'career_fair',
                career_fair_event: config.event || '', lead_interest: payload.lead_interest });
            try { trackLeadCreated(); } catch (_) { /* Analytics cannot prevent confirmation. */ }
            // Keep the legacy thank-you conversion page used by the existing contact flow.
            const frame = document.getElementById('thankYouFrame');
            if (frame) {
                try {
                    localStorage.setItem('userData', JSON.stringify({
                        email: payload.email, phone_number: payload.phone,
                    }));
                    frame.src = `/zonidirect/index-typ.html${window.location.search}`;
                } catch (_) { /* Storage/analytics restrictions cannot prevent confirmation. */ }
            }
            form.reset();
            syncEmailPreview();
            form.classList.remove('was-validated');
            showThanks(data.message);
        } catch (_) {
            if (!accepted) showError(error.dataset.error);
        } finally {
            busy = false;
            submit.disabled = submitted;
            label.hidden = false;
            loading.hidden = true;
            try { capClient?.reset?.(); } catch (_) { /* Retry needs a fresh token. */ }
            capClient = null;
        }
    });

    async function open() {
        applyConfig(await fetchConfig(new URLSearchParams(window.location.search).get('utm_campaign') || ''));
        phoneReady = initIntlPhone({ selector: '#career-fair-phone', instanceKey: 'careerFairPhone',
            errorSelector: '#career-fair-phone-error', invalidMessage: error.dataset.phoneError,
            onValidityChange: valid => syncPhone(valid) });
        phoneReady = phoneReady.then(async instance => {
            phone = instance;
            await instance?.promise;
            syncPhone();
        }).catch(() => { phone = null; syncPhone(false); });
        if (document.querySelector('.modal.show')) {
            document.addEventListener('hidden.bs.modal', () => {
                if (!document.querySelector('.modal.show')) Modal.getOrCreateInstance(modal).show();
            }, { once: true });
        } else Modal.getOrCreateInstance(modal).show();
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', open, { once: true });
    else open();
}
